import type { WalletTransactionDirection, WalletTransactionEntity } from "../types/wallets-api.types";

// Normalize legacy descriptions that embedded raw UUIDs / English boilerplate.
const UUID_REGEX = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/**
 * Strips embedded UUIDs from a movement description and tidies the leftover whitespace (collapses runs of
 * spaces, removes spaces before punctuation).
 */
export const sanitizeDescription = (raw: string): string => {
  return raw
    .replace(UUID_REGEX, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([.,;])/g, "$1")
    .trim();
};

/**
 * Whether a movement is incoming. The API serializes `direction` in lowercase (`"in"`/`"out"`), so compare
 * case-insensitively rather than against the uppercased TS type.
 */
export const isIncomingDirection = (direction: WalletTransactionDirection): boolean =>
  String(direction).toUpperCase() === "IN";

/**
 * Formats a movement amount as a signed currency string: `+` for incoming (IN), `−` (minus sign, not hyphen)
 * for outgoing (OUT). Falls back to USD when no currency.
 */
export const formatAmount = (amount: string, currency: string, direction: WalletTransactionDirection) => {
  const value = Math.abs(Number(amount));
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  const sign = isIncomingDirection(direction) ? "+" : "−";
  return `${sign}${formatted}`;
};

/** Formats a raw numeric (or numeric string) value as USD, or "—" when not finite. */
export const formatUSD = (raw: string | number) => {
  const value = typeof raw === "string" ? Number(raw) : raw;
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/** Like formatUSD but provider precision (DUSD/6): 2 decimals min, up to 6 so sub-cent balances show. */
export const formatCardBalance = (raw: string | number) => {
  const value = typeof raw === "string" ? Number(raw) : raw;
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(value);
};

/** Sums the absolute amounts of all movements flowing in the given direction. */
export const sumByDirection = (movements: WalletTransactionEntity[], direction: WalletTransactionDirection) =>
  movements
    .filter((m) => String(m.direction).toUpperCase() === String(direction).toUpperCase())
    .reduce((acc, m) => acc + Math.abs(Number(m.amount)), 0);
